import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePublic } from '@/contexts/PublicContext';
import useUserRole from '@/hooks/domain/useUserRole';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import useDetailModalStore from '@/stores/pure/DetailModalStore';
import useUIStore from '@/stores/pure/UIStore';

import { useFeedbackDetailContext } from './context/FeedbackDetailContext';
import type { FeedbackDetailModalProps } from './FeedbackDetailModal/FeedbackDetailModal';
import feedbackDetailUtils from './utils/feedbackDetailutils';

export type AttrState = {
  [key: string]: string | number | number[];
};

const useFeedbackDetail = (props: FeedbackDetailModalProps) => {
  const isPublic = usePublic();
  const { isEditor } = useUserRole();
  const fullView = useUIStore((state) => state.feedbackDetailFullView);
  const modalStack = useDetailModalStore((state) => state.modalStack);
  const [feedbackAlias, setFeedbackAlias] = useState(props.feedbackAlias);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const { detail, setDetail } = useFeedbackDetailContext();
  const setValue = useCallback(
    <T extends keyof FeedbackT>(key: T, value: FeedbackT[T]) => {
      if (!detail) return;
      setDetail({
        ...detail,
        [key]: value,
      });
    },
    [detail],
  );

  const paginationInfo = useFeedbackListStore((state) => state.paginationInfo);
  const changeTitle = useFeedbackListStore((state) => state.changeTitle);
  const fetchFeedbackList = useFeedbackListStore(
    (state) => state.fetchFeedbackList,
  );
  const updateFetchParams = useFeedbackListStore(
    (state) => state.updateFetchParams,
  );
  const fetchFeedbackDetail = useFeedbackListStore(
    (state) => state.fetchFeedbackDetail,
  );
  const { pageAliases, setPageAliases } = useFeedbackListStore((state) => ({
    pageAliases: state.pageAliases,
    setPageAliases: state.setPageAliases,
  }));
  const { prevAlias, nextAlias } = pageAliases;

  const maxPage = useMemo(() => {
    return Math.floor(paginationInfo.count / paginationInfo.size) - 1;
  }, [paginationInfo.count, paginationInfo.size]);

  const toNextFeedback = async () => {
    if (!props.tableKey) return;
    if (nextAlias) {
      setFeedbackAlias(nextAlias);
      setPageAliases(nextAlias);
    } else {
      if (paginationInfo.page > maxPage) return;
      updateFetchParams({ page: paginationInfo.page + 1 });
      const res = await fetchFeedbackList();
      if (!res.feedbacks || !res.feedbacks[0]) return;
      setFeedbackAlias(res.feedbacks[0].alias);
      setPageAliases(res.feedbacks[0].alias);
    }
  };

  const toPrevFeedback = async () => {
    if (!props.tableKey) return;
    if (prevAlias) {
      setFeedbackAlias(prevAlias);
      setPageAliases(prevAlias);
    } else {
      if (paginationInfo.page <= 0) return;
      updateFetchParams({ page: paginationInfo.page - 1 });
      const res = await fetchFeedbackList();
      if (!res.feedbacks) return;
      const targetIndex = res.feedbacks.length - 1;
      if (!res.feedbacks[targetIndex]) return;
      setFeedbackAlias(res.feedbacks[targetIndex].alias);
      setPageAliases(res.feedbacks[targetIndex].alias);
    }
  };

  useEffect(() => {
    setFeedbackAlias(props.feedbackAlias);
  }, [props.feedbackAlias]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setEmpty(false);
      if (!feedbackAlias) return;
      const fd = await fetchFeedbackDetail(feedbackAlias, isPublic);
      if (!fd) {
        setEmpty(true);
      } else {
        setDetail(fd);
      }
      return fd;
    } catch (e) {
      setEmpty(true);
    } finally {
      setLoading(false);
    }
  }, [feedbackAlias, isPublic]);

  useEffect(() => {
    if (loading) return;
    if (empty) return;
    fetchData();
  }, [fetchData]);

  const feedbackDetailFullView = useMemo(() => {
    return modalStack.length > 1 ? false : fullView;
  }, [fullView, modalStack.length]);

  const chatText: ChatMessageT[] = useMemo(() => {
    if (!detail?.source) return [];
    return feedbackDetailUtils.parseChatMessage(
      detail?.chat_text || [],
      detail?.source?.type,
    );
  }, [detail?.chat_text, detail?.source]);

  const chatTitle = useMemo(() => {
    if (!isEditor) return '';
    return (
      chatText?.find((chat) => chat.author?.type === 3)?.author?.name || ''
    );
  }, [isEditor, chatText]);

  const onBlurHandler = () => {
    if (detail) {
      changeTitle(detail.alias, detail?.title || 'Untitled');
    }
  };

  const closeModal = () => {
    setPageAliases(null);
    props.closeModal();
  };

  return {
    ...props,
    closeModal,
    feedbackAlias,
    detail,
    feedbackDetailFullView,
    chatText,
    chatTitle,
    empty,
    loading,
    onBlurHandler,
    changeTitle,
    toNextFeedback,
    toPrevFeedback,
    setValue,
  };
};

export default useFeedbackDetail;
